import { motion } from "framer-motion";
import { Users, Globe, Award, Target, CheckCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const TEAM_IMAGE = "https://images.unsplash.com/photo-1565688527174-775059ac429c?w=800";

const AboutPage = () => {
  const stats = [
    { value: "15+", label: "Years Experience" },
    { value: "500+", label: "Partner Agents" },
    { value: "100+", label: "Destinations" },
    { value: "10K+", label: "Happy Travelers" },
  ];

  const values = [
    { icon: Target, title: "Customer First", desc: "Every decision centers on delivering exceptional value to our partners and their clients." },
    { icon: CheckCircle, title: "Reliability", desc: "We stand by our commitments with transparent pricing and on-time service delivery." },
    { icon: Globe, title: "Global Reach", desc: "From tropical beaches to mountain peaks, we connect you to the world's best destinations." },
    { icon: Award, title: "Excellence", desc: "We continuously improve our offerings to maintain industry-leading standards." },
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-primary-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-secondary-500 font-accent text-xl">Our Story</span>
            <h1 className="font-heading text-4xl md:text-6xl text-white mt-2 mb-6">
              About Happy Holidays DMC
            </h1>
            <p className="text-white/80 max-w-2xl mx-auto text-lg">
              Your trusted B2B travel partner since 2010, connecting travel agents with world-class destinations and unforgettable experiences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white -mt-8 relative z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-xl shadow-xl p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <p className="text-4xl font-bold text-primary-700 mb-1">{stat.value}</p>
                  <p className="text-muted-foreground text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={TEAM_IMAGE} 
                alt="Our Team" 
                className="rounded-lg shadow-xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-secondary-500 font-accent text-xl">Who We Are</span>
              <h2 className="font-heading text-3xl md:text-4xl mt-2 mb-6">
                Your Success is Our Mission
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Founded in 2010, Happy Holidays DMC started with a simple vision: to become the most reliable B2B travel partner for agents across India. What began as a small operation has grown into a trusted name serving 500+ travel agents nationwide.
                </p>
                <p>
                  Our team of travel experts works tirelessly to curate exceptional packages, negotiate the best rates, and ensure seamless experiences for your clients. We understand the travel business because we've been in your shoes.
                </p>
                <p>
                  With dedicated support, competitive pricing, and a commitment to quality, we're here to help you grow your business while delivering unforgettable journeys to your clients.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-secondary-500 font-accent text-xl">Our Values</span>
            <h2 className="font-heading text-3xl md:text-4xl mt-2">What Drives Us</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-lg border border-border hover:border-primary-700/30 hover:shadow-lg transition-all"
              >
                <value.icon className="w-10 h-10 text-primary-700 mb-4" />
                <h3 className="font-heading text-xl mb-2">{value.title}</h3>
                <p className="text-muted-foreground text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-primary-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-secondary-500 font-accent text-xl">Why Partner With Us</span>
            <h2 className="font-heading text-3xl md:text-4xl mt-2">The Happy Holidays Advantage</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Competitive Rates", desc: "Direct contracts with hotels and airlines mean better margins for your business." },
              { title: "Fast Turnaround", desc: "Get quotes within 24 hours and instant confirmations on popular packages." },
              { title: "Dedicated Support", desc: "Your personal account manager is always a call away to assist with any queries." },
              { title: "Quality Assurance", desc: "All our partners are vetted to ensure top-notch service for your clients." },
              { title: "Flexible Packages", desc: "Customize any package to meet your client's specific needs and preferences." },
              { title: "Technology Driven", desc: "Our agent portal makes requesting quotes and managing bookings effortless." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary-700 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
